import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { Bot, MessagesSquare, Phone, Users, CalendarDays, Zap } from "lucide-react";

const KpiCard = ({ Icon, label, value, hint }) => (
  <div className="hs-card p-5" data-testid={`kpi-${label.toLowerCase().replace(/ /g, '-')}`}>
    <div className="flex items-center justify-between">
      <div className="text-[11px] font-mono uppercase tracking-wide text-[#4B5563]">{label}</div>
      <Icon className="w-4 h-4 text-[#4B5563]" />
    </div>
    <div className="font-display font-extrabold text-4xl mt-3">{value}</div>
    {hint && <div className="text-xs text-[#4B5563] mt-1">{hint}</div>}
  </div>
);

export default function Overview() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/analytics/overview").then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-sm text-[#4B5563]">Loading…</div>;

  const { kpis, series } = data;

  return (
    <div data-testid="dashboard-overview" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Overview</h1>
          <p className="text-sm text-[#4B5563]">Your control room at a glance.</p>
        </div>
        <Link to="/dashboard/agents" className="btn-primary text-sm" data-testid="overview-build-agent-cta">Build a new agent</Link>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <KpiCard Icon={Bot} label="Agents" value={kpis.agents} />
        <KpiCard Icon={MessagesSquare} label="Conversations" value={kpis.conversations} />
        <KpiCard Icon={Phone} label="Calls" value={kpis.calls} />
        <KpiCard Icon={Users} label="Leads" value={kpis.leads} />
        <KpiCard Icon={CalendarDays} label="Appointments" value={kpis.appointments} />
        <KpiCard Icon={Zap} label="AI Credits" value={kpis.ai_credits} hint={`voice: ${kpis.voice_credits}`} />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="hs-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-display font-bold text-lg">Activity – last 7 days</div>
              <div className="text-xs font-mono text-[#4B5563]">conversations + calls per day</div>
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "#4B5563", fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "#4B5563", fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ border: "1px solid #E2E8F0", borderRadius: 4, fontSize: 12 }} />
                <Line type="monotone" dataKey="conversations" stroke="#0044FF" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="calls" stroke="#030712" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="hs-card p-6">
          <div className="font-display font-bold text-lg">Quick start</div>
          <div className="text-xs font-mono text-[#4B5563]">3 steps to your first deployment</div>
          <ol className="text-sm mt-4 space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#0044FF] text-white text-xs font-bold flex items-center justify-center">1</span>
              <div>
                <div className="font-medium">Customise your agent</div>
                <Link to="/dashboard/agents" className="text-xs text-[#0044FF]" data-testid="qs-link-agents">Open agent builder →</Link>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#0044FF] text-white text-xs font-bold flex items-center justify-center">2</span>
              <div>
                <div className="font-medium">Train on your knowledge</div>
                <div className="text-xs text-[#4B5563]">Add your website URL or upload docs</div>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 bg-[#0044FF] text-white text-xs font-bold flex items-center justify-center">3</span>
              <div>
                <div className="font-medium">Embed or call</div>
                <Link to="/dashboard/calls" className="text-xs text-[#0044FF]" data-testid="qs-link-calls">Start outbound →</Link>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}
